import React, { useEffect, useMemo, useState } from "react";
import {
  Blocks,
  ShieldCheck,
  Copy,
  Check,
  ExternalLink,
  Terminal,
  Code2,
  FileJson,
  Anchor,
  GitBranch,
  Sparkles,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import {
  listTruthBlocks,
  getTruthCompilerStats,
  verifyTruthChain,
} from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// --------------------------------------------------------------------- //
// Code snippets — copy-paste runnable. The Architect's job is to look
// brilliant on first contact; the developer's job is to ship in 5 min.
// --------------------------------------------------------------------- //

const SNIPPET_CURL = `# 1. Fetch a block + inclusion proof for a single fact
BLOCK_ID="<a-block-id-from-/api/truth-blocks>"
CITATION_ID="<a-citation-id-from-that-block>"

curl -s "${BACKEND}/api/truth-blocks/$BLOCK_ID/proof/$CITATION_ID" | jq

# 2. Walk the entire chain server-side (cheap, ~400 bytes/block header)
curl -s "${BACKEND}/api/truth-blocks/verify" | jq

# 3. Preview which verified facts WOULD anchor for a question
curl -s "${BACKEND}/api/truth-compiler/recall?q=tallest+mountain+from+earths+center" | jq`;

const SNIPPET_PYTHON = `# pip install requests
import hashlib
import requests

BACKEND = "${BACKEND}"

def sha256(s: str) -> str:
    return hashlib.sha256(s.encode("utf-8")).hexdigest()

def verify_inclusion(block_id: str, citation_id: str) -> bool:
    """Verify a single fact is sealed in a Truth Block — no Sentinel
    secret required. Walks the Merkle path bottom-up and recomputes
    the root locally."""
    r = requests.get(f"{BACKEND}/api/truth-blocks/{block_id}/proof/{citation_id}")
    r.raise_for_status()
    data = r.json()

    running = data["leaf"]["leaf_hash"]
    for step in data["proof"]:
        sib = step["hash"]
        if step["position"] == "right":
            running = sha256(running + sib)
        else:
            running = sha256(sib + running)

    return running == data["merkle_root"]

# Example:
ok = verify_inclusion("<block-id>", "<citation-id>")
print("verified:", ok)`;

const SNIPPET_JS = `// Browser-side or Node 20+. No dependencies — uses WebCrypto.
const BACKEND = "${BACKEND}";

async function sha256Hex(text) {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function verifyInclusion(blockId, citationId) {
  const res = await fetch(
    \`\${BACKEND}/api/truth-blocks/\${blockId}/proof/\${citationId}\`
  );
  const data = await res.json();

  let running = data.leaf.leaf_hash;
  for (const step of data.proof) {
    running = step.position === "right"
      ? await sha256Hex(running + step.hash)
      : await sha256Hex(step.hash + running);
  }
  return running === data.merkle_root;
}

// Example:
const ok = await verifyInclusion("<block-id>", "<citation-id>");
console.log("verified:", ok);`;

const SNIPPET_RAG = `# Plug verified truths into your own LLM call (any provider).
# This gets you cryptographically-anchored answers in ~20 lines.

import requests
from openai import OpenAI  # or anthropic / google.genai

BACKEND = "${BACKEND}"
client = OpenAI()

def anchored_chat(question: str):
    # 1. Pull verified facts relevant to the question
    r = requests.get(
        f"{BACKEND}/api/truth-compiler/recall",
        params={"q": question, "k": 5},
    )
    fragment = r.json().get("prompt_fragment", "")

    # 2. Inject as system message — model defers to anchors
    resp = client.chat.completions.create(
        model="gpt-4o",
        messages=[
            {"role": "system", "content": (
                "You are a helpful assistant. When the user asks a factual "
                "question, defer to the anchored truths below over your "
                "training data, and cite the block hash when you do.\\n\\n"
                + fragment
            )},
            {"role": "user", "content": question},
        ],
    )
    return resp.choices[0].message.content`;

// --------------------------------------------------------------------- //
// Endpoint reference
// --------------------------------------------------------------------- //

const ENDPOINTS = [
  {
    group: "Truth Blocks · public",
    items: [
      {
        method: "GET",
        path: "/api/truth-blocks",
        auth: "none",
        purpose: "List sealed blocks newest-first. Pass ?full=true to inline leaves.",
        params: "limit (1-200, default 50), full (bool)",
      },
      {
        method: "GET",
        path: "/api/truth-blocks/verify",
        auth: "none",
        purpose: "Walk the entire chain server-side; returns {valid, verified, reason}.",
        params: "—",
      },
      {
        method: "GET",
        path: "/api/truth-blocks/pending",
        auth: "none",
        purpose: "Preview of citations waiting to be sealed into the next block.",
        params: "—",
      },
      {
        method: "GET",
        path: "/api/truth-blocks/{block_id}",
        auth: "none",
        purpose: "Full block (header + all leaves) with self-verification result.",
        params: "—",
      },
      {
        method: "GET",
        path: "/api/truth-blocks/{block_id}/proof/{citation_id}",
        auth: "none",
        purpose: "Merkle inclusion proof — verify a single fact without trusting our DB.",
        params: "—",
      },
      {
        method: "POST",
        path: "/api/truth-blocks/seal",
        auth: "architect",
        purpose: "Force-seal pending citations into a new block.",
        params: "—",
      },
    ],
  },
  {
    group: "Truth Compiler · public",
    items: [
      {
        method: "GET",
        path: "/api/truth-compiler/stats",
        auth: "none",
        purpose: "Index size + drift between Mongo source-of-truth and Chroma index.",
        params: "—",
      },
      {
        method: "GET",
        path: "/api/truth-compiler/recall",
        auth: "none",
        purpose: "Top-K verified facts for any query + the ready-to-inject prompt fragment.",
        params: "q (required), k (1-10, default 5)",
      },
    ],
  },
  {
    group: "Truth Ledger · public",
    items: [
      {
        method: "GET",
        path: "/api/ledger/citations",
        auth: "none",
        purpose: "Every triangulated / web-grounded citation, filterable by epistemic status.",
        params: "status (fact|web_cited|contested|theory|refuted|challenged), limit",
      },
      {
        method: "GET",
        path: "/api/citations/{citation_id}/lineage",
        auth: "none",
        purpose: "Full challenge + triangulation + grounding history with chain self-verification.",
        params: "—",
      },
    ],
  },
];

// --------------------------------------------------------------------- //
// Components
// --------------------------------------------------------------------- //

function CopyButton({ value, label = "Copy", testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          toast.success("Copied");
          setTimeout(() => setDone(false), 1200);
        } catch {
          toast.error("Clipboard unavailable");
        }
      }}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-800 hover:border-emerald-500/60 text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-emerald-300 transition-colors font-mono"
      title={label}
    >
      {done ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
      {done ? "copied" : label}
    </button>
  );
}

function CodeBlock({ code, language, testid }) {
  return (
    <div className="relative border border-zinc-800 bg-[#06070a]" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-zinc-900 px-3 py-1.5">
        <span className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 font-mono">{language}</span>
        <CopyButton value={code} label="copy code" testid={`${testid}-copy`} />
      </div>
      <pre className="overflow-x-auto px-4 py-3 text-[12px] leading-relaxed text-zinc-200 font-mono">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function MethodBadge({ method }) {
  const map = {
    GET: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
    POST: "border-sky-500/40 text-sky-300 bg-sky-500/5",
    DELETE: "border-rose-500/40 text-rose-300 bg-rose-500/5",
    PUT: "border-amber-500/40 text-amber-300 bg-amber-500/5",
  };
  return (
    <span className={`px-2 py-0.5 text-[9.5px] uppercase tracking-[0.25em] border font-mono ${map[method] || "border-zinc-700 text-zinc-300"}`}>
      {method}
    </span>
  );
}

function AuthBadge({ auth }) {
  if (auth === "architect") {
    return (
      <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 bg-amber-500/5">
        architect
      </span>
    );
  }
  return (
    <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-zinc-700 text-zinc-400">
      public
    </span>
  );
}

function EndpointRow({ ep }) {
  const url = `${BACKEND}${ep.path.replace(/{[^}]+}/g, (m) => m)}`;
  const isPublic = ep.auth === "none";
  return (
    <div className="border border-zinc-800 bg-[#0a0a0c] p-4" data-testid={`endpoint-${ep.method}-${ep.path}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <MethodBadge method={ep.method} />
        <AuthBadge auth={ep.auth} />
        <code className="font-mono text-[12.5px] text-zinc-100 break-all">{ep.path}</code>
        {isPublic && !ep.path.includes("{") && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-emerald-400 hover:text-emerald-300"
            data-testid={`open-${ep.path}`}
          >
            <ExternalLink className="h-3 w-3" /> try live
          </a>
        )}
      </div>
      <p className="text-sm text-zinc-300 leading-snug">{ep.purpose}</p>
      {ep.params && ep.params !== "—" && (
        <div className="mt-2 text-[10.5px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          params · <span className="text-zinc-300 normal-case">{ep.params}</span>
        </div>
      )}
    </div>
  );
}

export default function ApiDocs() {
  const [stats, setStats] = useState({ blocks: null, compiler: null, chain: null, supply: null });
  const [activeLang, setActiveLang] = useState("curl");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listTruthBlocks({ limit: 1 }).catch(() => null),
      getTruthCompilerStats().catch(() => null),
      verifyTruthChain().catch(() => null),
      fetch(`${BACKEND}/api/aegis/supply`).then((r) => r.json()).catch(() => null),
    ]).then(([blocks, compiler, chain, supply]) => {
      if (cancelled) return;
      setStats({ blocks, compiler, chain, supply });
    });
    return () => { cancelled = true; };
  }, []);

  const liveSnippet = useMemo(() => {
    if (activeLang === "curl") return SNIPPET_CURL;
    if (activeLang === "python") return SNIPPET_PYTHON;
    if (activeLang === "js") return SNIPPET_JS;
    return SNIPPET_RAG;
  }, [activeLang]);

  const liveLanguageLabel = useMemo(() => ({
    curl: "bash · curl",
    python: "python · requests",
    js: "javascript · webcrypto",
    rag: "python · openai + truth compiler",
  }[activeLang]), [activeLang]);

  return (
    <div className="min-h-screen bg-[#06070a] text-zinc-100">
      {/* Hero */}
      <div className="border-b border-zinc-900 bg-gradient-to-b from-[#0a1014] to-[#06070a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-[10px] uppercase tracking-[0.4em] text-emerald-400 mb-3 inline-flex items-center gap-2">
            <Blocks className="h-3 w-3" /> △ Projectivy Sentinel · Public API
          </div>
          <div className="flex flex-wrap items-start gap-6">
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-4xl sm:text-5xl tracking-[0.12em] text-zinc-100 leading-tight">
                Verified Truth, as an API.
              </h1>
              <p className="mt-4 text-zinc-300 text-base sm:text-lg max-w-3xl leading-relaxed">
                Every fact in our chain has been cross-verified by independent
                frontier LLMs, signed with HMAC, sealed into a Merkle-rooted block,
                and made retrievable for any downstream system. Verified claims
                mint <span className="text-amber-300 font-mono">AEGIS</span>, the
                native proof-of-cognitive-labor coin. No auth required — this is
                a public good.
              </p>
            </div>
            <img
              src="/brand/aegis-token-square.jpg"
              alt="Aegis Peace Token"
              className="hidden md:block w-44 h-44 rounded-sm border border-amber-500/40 shadow-[0_0_60px_-12px_rgba(251,191,36,0.4)] object-cover"
              data-testid="aegis-token-hero"
            />
          </div>

          {/* Live stats */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-2 max-w-4xl">
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="stat-blocks">
              <div className="text-[9px] uppercase tracking-[0.3em] text-emerald-400/80">Blocks sealed</div>
              <div className="font-mono text-2xl text-emerald-300 mt-1">
                {stats.blocks?.count ?? "…"}
              </div>
            </div>
            <div className="border border-sky-500/30 bg-sky-500/5 p-3" data-testid="stat-facts">
              <div className="text-[9px] uppercase tracking-[0.3em] text-sky-400/80">Verified facts</div>
              <div className="font-mono text-2xl text-sky-300 mt-1">
                {stats.compiler?.total_indexed ?? "…"}
              </div>
            </div>
            <div className="border border-violet-500/30 bg-violet-500/5 p-3" data-testid="stat-pending">
              <div className="text-[9px] uppercase tracking-[0.3em] text-violet-400/80">Pending leaves</div>
              <div className="font-mono text-2xl text-violet-300 mt-1">
                {stats.blocks?.pending_leaves ?? "…"}
              </div>
            </div>
            <div className="border border-amber-500/30 bg-amber-500/5 p-3 relative overflow-hidden" data-testid="stat-aegis-supply">
              <img
                src="/brand/aegis-token-icon.png"
                alt=""
                aria-hidden="true"
                className="absolute right-0 bottom-0 h-12 w-12 opacity-30"
              />
              <div className="text-[9px] uppercase tracking-[0.3em] text-amber-400/80 relative">AEGIS supply</div>
              <div className="font-mono text-2xl text-amber-300 mt-1 relative">
                {stats.supply?.total_minted ?? "…"}
              </div>
            </div>
            <div
              className={`border p-3 ${stats.chain?.chain?.valid ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`}
              data-testid="stat-chain-status"
            >
              <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500">Chain status</div>
              <div className={`font-mono text-lg mt-1 ${stats.chain?.chain?.valid ? "text-emerald-300" : "text-rose-300"}`}>
                {stats.chain ? (stats.chain.chain.valid ? "✓ VERIFIED" : "✗ BROKEN") : "…"}
              </div>
            </div>
          </div>

          {/* Three big claims */}
          <div className="mt-10 grid sm:grid-cols-3 gap-3">
            <div className="border border-zinc-800 p-4 bg-[#08090c]">
              <div className="text-emerald-400 mb-1.5"><ShieldCheck className="h-4 w-4" /></div>
              <div className="font-heading tracking-[0.08em] text-zinc-100 text-base mb-1">Independent verification</div>
              <p className="text-[12.5px] text-zinc-400 leading-relaxed">
                Claude AND GPT both have to agree on a claim, with Gemini as the arbiter,
                before it earns the <code className="text-emerald-300">fact</code> tier.
              </p>
            </div>
            <div className="border border-zinc-800 p-4 bg-[#08090c]">
              <div className="text-sky-400 mb-1.5"><Layers className="h-4 w-4" /></div>
              <div className="font-heading tracking-[0.08em] text-zinc-100 text-base mb-1">Merkle inclusion proofs</div>
              <p className="text-[12.5px] text-zinc-400 leading-relaxed">
                Verify a single fact in <code className="text-sky-300">O(log n)</code>{" "}
                without downloading the whole block. No Sentinel secret required.
              </p>
            </div>
            <div className="border border-zinc-800 p-4 bg-[#08090c]">
              <div className="text-violet-400 mb-1.5"><Anchor className="h-4 w-4" /></div>
              <div className="font-heading tracking-[0.08em] text-zinc-100 text-base mb-1">Plug into your own LLM</div>
              <p className="text-[12.5px] text-zinc-400 leading-relaxed">
                Pull <code className="text-violet-300">/api/truth-compiler/recall</code> and
                inject the prompt fragment into <em>any</em> model — anchored answers in 20 lines.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-14">
        {/* Quick verification recipe */}
        <section data-testid="section-verify-recipe">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400 mb-1 flex items-center gap-2">
                <ShieldCheck className="h-3 w-3" /> 60-second start
              </div>
              <h2 className="font-heading text-2xl tracking-[0.1em] text-zinc-100">Verify a fact yourself</h2>
              <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
                Walk a short path of sibling SHA-256 hashes and recompute the
                Merkle root. If it matches, the fact is in the block and the
                block is signed by our chain. No trust required.
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              {[
                { id: "curl",   label: "curl",   icon: Terminal },
                { id: "python", label: "Python", icon: Code2 },
                { id: "js",     label: "JS",     icon: FileJson },
                { id: "rag",    label: "RAG plug-in", icon: Anchor },
              ].map((t) => {
                const Icon = t.icon;
                const active = activeLang === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveLang(t.id)}
                    data-testid={`lang-tab-${t.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10.5px] uppercase tracking-[0.2em] border transition-colors ${
                      active
                        ? "border-emerald-500/70 text-emerald-200 bg-emerald-500/10"
                        : "border-zinc-800 text-zinc-400 hover:border-emerald-500/40 hover:text-emerald-300"
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <CodeBlock code={liveSnippet} language={liveLanguageLabel} testid="verify-snippet" />
          <div className="mt-3 text-[10.5px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
            base url · <span className="text-zinc-300 normal-case">{BACKEND}</span>
            <CopyButton value={BACKEND} label="copy base url" testid="copy-base-url" />
          </div>
        </section>

        {/* The "what is a leaf" diagram */}
        <section data-testid="section-leaf-schema">
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.35em] text-sky-400 mb-1 flex items-center gap-2">
              <GitBranch className="h-3 w-3" /> data model
            </div>
            <h2 className="font-heading text-2xl tracking-[0.1em] text-zinc-100">Anatomy of a Truth Block</h2>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
              Two cryptographic primitives wired together. Inside a block — a Merkle tree.
              Between blocks — a previous_hash chain. Both are public, both verify in any language.
            </p>
          </div>
          <CodeBlock
            code={`Block #N
├── id, height, sealed_at
├── previous_hash      ◀── chains to block #N-1
├── merkle_root        ◀── commits to every leaf
├── leaves[ ]
│     ├── citation_id, claim_sha256
│     ├── epistemic_status: "fact" | "web_cited"
│     ├── verdict_signature_sha256   (HMAC of triangulation verdict)
│     ├── triangulation_agreeing: ["claude", "gpt"]
│     ├── grounding_source_count, primary source URI
│     └── leaf_hash    ◀── SHA-256 over the canonical leaf payload
├── depth_of_truth     ◀── APT reward score (Claude+GPT agree = 100 pts)
├── epistemic_distribution: { fact: n, web_cited: n, ... }
├── seal_signature_sha256   ◀── HMAC over canonical header
└── block_hash             ◀── SHA-256(canonical_header || signature)`}
            language="schema · truth block"
            testid="block-schema"
          />
        </section>

        {/* Endpoints */}
        <section data-testid="section-endpoints">
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.35em] text-violet-400 mb-1 flex items-center gap-2">
              <Sparkles className="h-3 w-3" /> reference
            </div>
            <h2 className="font-heading text-2xl tracking-[0.1em] text-zinc-100">All endpoints</h2>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
              Every <span className="text-emerald-300">public</span> endpoint is open — no auth, no rate
              limits beyond global fairness throttles. <span className="text-amber-300">Architect-only</span>{" "}
              endpoints exist for ceremony moments (e.g. force-sealing a block at a keynote).
            </p>
          </div>
          <div className="space-y-6">
            {ENDPOINTS.map((g) => (
              <div key={g.group}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 font-mono">{g.group}</div>
                <div className="grid gap-2">
                  {g.items.map((ep) => (
                    <EndpointRow key={`${ep.method}-${ep.path}`} ep={ep} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA / license */}
        <section className="border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="section-cta">
          <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400 mb-2">plug in</div>
          <h3 className="font-heading text-xl tracking-[0.1em] text-zinc-100 mb-2">
            Building something that needs verified facts?
          </h3>
          <p className="text-zinc-300 text-sm leading-relaxed max-w-2xl">
            The chain is a public good. Mirror it, audit it, plug it into your own RAG —
            we want you using it. The only &ldquo;license&rdquo; is: when you cite a verified fact,
            reference the block hash so your users can verify cryptographically. That&apos;s it.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="/truth-blocks"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10.5px] uppercase tracking-[0.2em] border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
              data-testid="cta-open-chain"
            >
              <Blocks className="h-3.5 w-3.5" /> Open the chain
            </a>
            <a
              href={`${BACKEND}/api/truth-blocks`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10.5px] uppercase tracking-[0.2em] border border-zinc-700 text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-300"
              data-testid="cta-raw-json"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Raw JSON
            </a>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="border-t border-zinc-900 px-4 sm:px-6 py-6">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-mono">
          <span>△ Projectivy Sentinel · Proof-of-Verification v1</span>
          <span>
            <a className="hover:text-emerald-400" href="/ledger">truth ledger</a>
            {" · "}
            <a className="hover:text-emerald-400" href="/truth-blocks">truth blocks</a>
            {" · "}
            <a className="hover:text-emerald-400" href="/">home</a>
          </span>
        </div>
      </div>
    </div>
  );
}
